import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useOrganization } from "../context/OrganizationContext";
import apiService from "../services/apiService";
const BillingHistory = () => {
  const { currentOrganization } = useOrganization();
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadBillingHistory = useCallback(async () => {
    if (!currentOrganization?.id) return;
    try {
      setLoading(true);
      const [historyData, summaryData] = await Promise.all([
        apiService.getSubscriptionHistory(currentOrganization.id),
        apiService.getSubscriptionHistorySummary(currentOrganization.id)
      ]);
      setHistory(historyData);
      setSummary(summaryData);
    } catch (error) {
      console.error("Failed to load billing history:", error);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (currentOrganization) {
      loadBillingHistory();
    }
  }, [currentOrganization, loadBillingHistory]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getReasonBadge = (reason) => {
    const badges = {
      upgraded: {
        color:
          "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
        icon: "↑",
        label: "Upgraded"
      },
      downgraded: {
        color:
          "border-amber-500/25 bg-amber-500/10 text-amber-200",
        icon: "↓",
        label: "Downgraded"
      },
      expired: {
        color: "border-white/15 bg-white/[0.06] text-white/60",
        icon: "⏱",
        label: "Expired"
      },
      canceled: {
        color: "border-red-500/25 bg-red-500/10 text-red-300",
        icon: "✕",
        label: "Canceled"
      },
      payment_failed: {
        color: "border-red-500/25 bg-red-500/10 text-red-300",
        icon: "⚠",
        label: "Payment failed"
      },
      other: {
        color: "border-white/15 bg-white/[0.06] text-white/60",
        icon: "•",
        label: "Other"
      }
    };

    const badge = badges[reason] || badges.other;
    return (
      <span className={`font-secondary inline-flex items-center space-x-1 rounded-sm border px-3 py-1 text-xs font-medium ${badge.color}`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="py-12 text-center"
      >
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-[#0c0c0c]">
          <svg className="h-8 w-8 text-primary/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="font-title mb-2 text-xl font-semibold text-white">No billing history</h3>
        <p className="font-secondary mx-auto max-w-md text-sm text-white/65">
          Your billing history will appear here once you upgrade or change your subscription plan.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 md:gap-4">
          <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4">
            <p className="font-secondary mb-1 text-xs text-white/50 sm:text-sm">Total subscriptions</p>
            <p className="font-title text-2xl font-bold text-white">{summary.total_subscriptions}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4">
            <p className="font-secondary mb-1 text-xs text-white/50 sm:text-sm">Total cost</p>
            <p className="font-title text-2xl font-bold text-emerald-300">${summary.total_cost}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4">
            <p className="font-secondary mb-1 text-xs text-white/50 sm:text-sm">Total days</p>
            <p className="font-title text-2xl font-bold text-white">{summary.total_days}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-4">
            <p className="font-secondary mb-1 text-xs text-white/50 sm:text-sm">Avg. duration</p>
            <p className="font-title text-2xl font-bold text-white">{summary.average_subscription_days} days</p>
          </div>
        </div>
      )}

      {/* History Table */}
      <div className="overflow-hidden rounded-sm border border-white/10 bg-[#0c0c0c]">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-6 py-4 font-secondary text-sm font-semibold text-white/50">Plan</th>
                <th className="text-left px-6 py-4 font-secondary text-sm font-semibold text-white/50">Started</th>
                <th className="text-left px-6 py-4 font-secondary text-sm font-semibold text-white/50">Ended</th>
                <th className="text-left px-6 py-4 font-secondary text-sm font-semibold text-white/50">Duration</th>
                <th className="text-left px-6 py-4 font-secondary text-sm font-semibold text-white/50">Cost</th>
                <th className="text-left px-6 py-4 font-secondary text-sm font-semibold text-white/50">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {history.map((record, index) => (
                <motion.tr
                  key={record.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="transition-colors hover:bg-white/[0.03]"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-primary/10">
                        <span className="text-sm font-bold text-primary">
                          {record.plan_details?.name?.charAt(0) || "P"}
                        </span>
                      </div>
                      <div>
                        <p className="font-secondary font-medium text-white">{record.plan_details?.name || "Unknown plan"}</p>
                        <p className="font-secondary text-xs text-white/45">
                          ${record.plan_details?.price_usd || "0.00"}/month
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="font-secondary px-6 py-4 text-sm text-white/75">
                    {formatDate(record.started_at)}
                  </td>
                  <td className="font-secondary px-6 py-4 text-sm text-white/75">
                    {formatDate(record.ended_at)}
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-secondary font-medium text-white">{record.duration_days} days</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-secondary font-semibold text-emerald-300">
                      ${parseFloat(record.cost || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {getReasonBadge(record.reason)}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Info */}
      <div className="flex flex-col gap-2 border-t border-white/10 pt-4 font-secondary text-sm text-white/45 sm:flex-row sm:items-center sm:justify-between">
        <p>Showing {history.length} {history.length === 1 ? "record" : "records"}</p>
        <p className="flex items-center gap-2">
          <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Last updated: {new Date().toLocaleDateString()}</span>
        </p>
      </div>
    </motion.div>
  );
};

export default BillingHistory;

